import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { subscriptionApi } from '../services/subscriptionApi';

interface ContractData {
  contractId: string;
  customerId: string;
  amount: number;
}

const Payment: React.FC = () => {
  const navigate = useNavigate();
  const [contractData, setContractData] = useState<ContractData | null>(null);
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardType, setCardType] = useState('');

  useEffect(() => {
    const data = sessionStorage.getItem('contractData');
    if (data) {
      setContractData(JSON.parse(data));
    } else {
      navigate('/subscription');
    }
  }, [navigate]);

  useEffect(() => {
    const cardNumber = paymentData.cardNumber.replace(/\s/g, '');
    if (cardNumber.startsWith('4')) {
      setCardType('Visa');
    } else if (cardNumber.startsWith('5') || cardNumber.startsWith('2')) {
      setCardType('Mastercard');
    } else if (cardNumber.startsWith('3')) {
      setCardType('American Express');
    } else if (cardNumber.startsWith('6')) {
      setCardType('Discover');
    } else {
      setCardType('');
    }
  }, [paymentData.cardNumber]);

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setPaymentData(prev => ({ ...prev, cardNumber: formatted }));
  };

  const validatePayment = () => {
    const errors: string[] = [];
    
    if (!paymentData.cardNumber || paymentData.cardNumber.replace(/\s/g, '').length < 13) {
      errors.push('Valid card number is required');
    }
    
    if (!paymentData.expiryMonth || !paymentData.expiryYear) {
      errors.push('Expiry date is required');
    }
    
    if (!paymentData.cvv || paymentData.cvv.length < 3) {
      errors.push('Valid CVV is required');
    }
    
    if (!paymentData.cardholderName.trim()) {
      errors.push('Cardholder name is required');
    }
    
    if (!paymentData.billingAddress.trim()) {
      errors.push('Billing address is required');
    }
    
    if (!paymentData.billingCity.trim()) {
      errors.push('Billing city is required');
    }
    
    if (!paymentData.billingState.trim()) {
      errors.push('Billing state is required');
    }
    
    if (!paymentData.billingZip.trim()) {
      errors.push('Billing ZIP code is required');
    }

    return errors;
  };

  const handlePayment = async () => {
    const validationErrors = validatePayment();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    if (!contractData) {
      setError('Contract data not found');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cardToken = subscriptionApi.createCardToken({
        cardNumber: paymentData.cardNumber.replace(/\s/g, ''),
        expiryMonth: paymentData.expiryMonth,
        expiryYear: paymentData.expiryYear,
        cvv: paymentData.cvv,
        cardholderName: paymentData.cardholderName
      });

      const paymentResponse = await subscriptionApi.processPayment({
        contractId: contractData.contractId,
        customerId: contractData.customerId,
        amount: contractData.amount,
        cardToken: cardToken,
        cardLast4: paymentData.cardNumber.slice(-4)
      });

      sessionStorage.setItem('paymentSuccess', JSON.stringify({
        paymentId: paymentResponse.paymentId,
        transactionId: paymentResponse.transactionId,
        amount: contractData.amount
      }));

      navigate('/success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment processing failed');
    } finally {
      setLoading(false);
    }
  };

  if (!contractData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading payment information...</p>
        </div>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear + i);
  const months = [
    { value: '01', label: '01' },
    { value: '02', label: '02' },
    { value: '03', label: '03' },
    { value: '04', label: '04' },
    { value: '05', label: '05' },
    { value: '06', label: '06' },
    { value: '07', label: '07' },
    { value: '08', label: '08' },
    { value: '09', label: '09' },
    { value: '10', label: '10' },
    { value: '11', label: '11' },
    { value: '12', label: '12' }
  ];

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-white shadow-sm">
        <div className="container py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/contract')}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h1 className="text-xl font-bold">Payment Information</h1>
            <div className="w-16"></div>
          </div>
        </div>
      </div>

      <div className="container py-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-red-50 to-red-100 px-6 py-4 text-center">
              <CreditCard className="w-12 h-12 text-red-600 mx-auto mb-2" />
              <h2 className="text-2xl font-bold mb-1">Secure Payment</h2>
              <p className="text-gray-600">Complete your subscription with secure payment processing</p>
            </div>

            <div className="p-6">
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-bold mb-4">Payment Details</h3>
                    
                    <form className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-3 text-sm">Card Information</h4>
                        
                        <div className="form-group">
                          <label className="form-label">Card Number *</label>
                          <div className="relative">
                            <input
                              type="text"
                              className="form-input pr-20"
                              value={paymentData.cardNumber}
                              onChange={handleCardNumberChange}
                              placeholder="1234 5678 9012 3456"
                              maxLength={19}
                            />
                            {cardType && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs font-medium text-gray-600">
                                {cardType}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <div className="form-group">
                            <label className="form-label">Month *</label>
                            <select
                              className="form-select"
                              value={paymentData.expiryMonth}
                              onChange={(e) => setPaymentData(prev => ({ ...prev, expiryMonth: e.target.value }))}
                            >
                              <option value="">MM</option>
                              {months.map(month => (
                                <option key={month.value} value={month.value}>{month.value}</option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Year *</label>
                            <select
                              className="form-select"
                              value={paymentData.expiryYear}
                              onChange={(e) => setPaymentData(prev => ({ ...prev, expiryYear: e.target.value }))}
                            >
                              <option value="">YYYY</option>
                              {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                              ))}
                            </select>
                          </div>

                          <div className="form-group">
                            <label className="form-label">CVV *</label>
                            <input
                              type="text"
                              className="form-input"
                              value={paymentData.cvv}
                              onChange={(e) => setPaymentData(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                              placeholder="123"
                              maxLength={4}
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Cardholder Name *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={paymentData.cardholderName}
                            onChange={(e) => setPaymentData(prev => ({ ...prev, cardholderName: e.target.value }))}
                            placeholder="John Doe"
                          />
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold mb-3 text-sm">Billing Address</h4>
                        
                        <div className="form-group">
                          <label className="form-label">Address *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={paymentData.billingAddress}
                            onChange={(e) => setPaymentData(prev => ({ ...prev, billingAddress: e.target.value }))}
                            placeholder="123 Main Street"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="form-group">
                            <label className="form-label">City *</label>
                            <input
                              type="text"
                              className="form-input"
                              value={paymentData.billingCity}
                              onChange={(e) => setPaymentData(prev => ({ ...prev, billingCity: e.target.value }))}
                              placeholder="Washington"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">State *</label>
                            <select
                              className="form-select"
                              value={paymentData.billingState}
                              onChange={(e) => setPaymentData(prev => ({ ...prev, billingState: e.target.value }))}
                            >
                              <option value="">Select</option>
                              {states.map(state => (
                                <option key={state} value={state}>{state}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label className="form-label">ZIP Code *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={paymentData.billingZip}
                            onChange={(e) => setPaymentData(prev => ({ ...prev, billingZip: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                            placeholder="20001"
                          />
                        </div>
                      </div>

                      {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <div className="text-red-700 text-xs">{error}</div>
                        </div>
                      )}
                    </form>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
                    <h3 className="text-lg font-bold mb-3 text-red-900">Order Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-red-800">Monthly Service Fee</span>
                        <span className="font-semibold text-red-900">${contractData.amount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-red-800">Setup Fee</span>
                        <span className="font-semibold text-red-900">$0</span>
                      </div>
                      <div className="border-t border-red-300 pt-2 mt-2">
                        <div className="flex justify-between text-base font-bold">
                          <span className="text-red-900">Total Due Today</span>
                          <span className="text-red-900">${contractData.amount}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <Lock className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-green-900 mb-1 text-sm">Secure Payment</h4>
                        <div className="text-green-800 text-xs space-y-1">
                          <div>• 256-bit SSL encryption</div>
                          <div>• PCI DSS compliant</div>
                          <div>• No card data stored</div>
                          <div>• Fraud protection</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-1 text-sm">30-Day Guarantee</h4>
                        <p className="text-blue-800 text-xs">
                          Not satisfied? Cancel within 30 days for a full refund. 
                          No questions asked.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={handlePayment}
                  disabled={loading}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing Payment...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Complete Payment - ${contractData.amount}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;